import * as React from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  ChevronDown,
  CreditCard,
  FileText,
  Gauge,
  LayoutGrid,
  LifeBuoy,
  LogOut,
  Megaphone,
  Menu,
  MessagesSquare,
  PanelLeftClose,
  Search,
  Settings,
  DoorOpen,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
  Wrench,
  X,
} from 'lucide-react';
import { useAuthStore, selectActiveSociety } from '../auth/auth.store';
import { useLiveUpdates } from '../realtime/use-live-updates';
import { useDateFormatSetting } from '../lib/use-date-format';
import { useBranding, useBrandAssets, useBrandDisplay, useBrandLogoHeight } from '../hooks/use-branding';
import { usePlatformGeneral } from '../hooks/use-platform-general';
import { useLocaleSetting } from '../../i18n/use-locale';
import { LanguageSwitch } from '../../i18n/LanguageSwitch';
import { CurrencySwitch, useCurrencyStore } from '../ui/currency-switch';
import { NotificationsPanel } from '../ui/notifications-panel';
import { Avatar, Badge, Button, cn } from '../ui/primitives';
import { FullscreenToggle, ThemeToggle } from '../theme/ThemeControls';
import { useBootstrap } from '../hooks/use-bootstrap';
import { t } from '../../i18n';

/**
 * The society admin shell.
 *
 * Layout decisions that matter:
 *   • The sidebar collapses to icons rather than disappearing — a facility
 *     manager on a 13" laptop still needs one-click navigation.
 *   • Trial, read-only and impersonation banners sit ABOVE the content, not in
 *     a toast, because they change what the user is allowed to do.
 *   • Nav items are filtered by role and by plan, so nothing visible leads to
 *     a 403 or a 402.
 */

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Lowest role that may see this. Omitted = every signed-in member. */
  role?: string;
  /** Plan feature key required. Omitted = always available. */
  feature?: string;
  /** Rendered as a count badge when non-zero. */
  badgeKey?: 'pendingApprovals' | 'openComplaints' | 'pendingBookings';
}

const NAV_SECTIONS: { heading: string; items: NavItem[] }[] = [
  {
    get heading() { return t('Overview'); },
    items: [{ to: '/dashboard', get label() { return t('Dashboard'); }, icon: Gauge }],
  },
  {
    get heading() { return t('Community'); },
    items: [
      { to: '/members', get label() { return t('Members'); }, icon: Users, badgeKey: 'pendingApprovals' },
      { to: '/announcements', get label() { return t('Notices'); }, icon: Megaphone },
      { to: '/community', get label() { return t('Community'); }, icon: MessagesSquare },
      { to: '/complaints', get label() { return t('Helpdesk'); }, icon: Wrench, badgeKey: 'openComplaints' },
      { to: '/visitors', get label() { return t('Visitors'); }, icon: ShieldCheck, feature: 'visitor_management' },
      { to: '/gate', get label() { return t('Gate'); }, icon: DoorOpen, role: 'FACILITY_MANAGER' },
      { to: '/helpers', get label() { return t('Daily helpers'); }, icon: Sparkles, feature: 'daily_helpers' },
    ],
  },
  {
    get heading() { return t('Facilities'); },
    items: [
      { to: '/amenities', get label() { return t('Amenities'); }, icon: CalendarDays, feature: 'amenity_booking', badgeKey: 'pendingBookings' },
      { to: '/documents', get label() { return t('Documents'); }, icon: FileText, feature: 'documents' },
      { to: '/structure', get label() { return t('Blocks & flats'); }, icon: Building2, role: 'FACILITY_MANAGER' },
    ],
  },
  {
    get heading() { return t('Finance'); },
    items: [
      { to: '/bills', get label() { return t('Bills'); }, icon: Wallet, role: 'TREASURER' },
      { to: '/payments', get label() { return t('Payments'); }, icon: CreditCard, role: 'TREASURER' },
      { to: '/accounting', get label() { return t('Accounting'); }, icon: LayoutGrid, role: 'TREASURER' },
      { to: '/reports', get label() { return t('Reports'); }, icon: BarChart3 },
    ],
  },
  {
    get heading() { return t('Society'); },
    items: [
      { to: '/subscription', get label() { return t('Subscription'); }, icon: CreditCard, role: 'SOCIETY_ADMIN' },
      { to: '/support', get label() { return t('Support'); }, icon: LifeBuoy, role: 'SOCIETY_ADMIN' },
      { to: '/settings', get label() { return t('Settings'); }, icon: Settings, role: 'SOCIETY_ADMIN' },
    ],
  },
];

const SIDEBAR_KEY = 'kinshipos.sidebarCollapsed';

export function AdminShell(): React.JSX.Element {
  // Opens the socket and keeps the console in step with the server.
  useLiveUpdates();
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, roles, isImpersonating, impersonationEndsAt, hasRole, signOut } = useAuthStore();
  const activeSociety = useAuthStore(selectActiveSociety);
  const { bootstrap, counts } = useBootstrap();

  // Applies the platform-wide date format to every date in the app, and
  // renders every date in this society's own timezone.
  useDateFormatSetting(bootstrap?.society.timezone);
  // Money is drawn in the society's own currency. Applied here, once, because
  // the 45 places that print an amount simply ask for "money" — the alternative
  // was threading a currency through every one of them.
  const setOwnCurrency = useCurrencyStore((state) => state.setOwn);
  const setViewCurrencyChoice = useCurrencyStore((state) => state.set);
  React.useEffect(() => {
    if (bootstrap?.society.currency) setOwnCurrency(bootstrap.society.currency);
  }, [bootstrap?.society.currency, setOwnCurrency]);
  // A society has one currency, so there is nothing to "view it in" — the bar
  // here sets what residents are billed. A choice left in this browser by the
  // platform console would otherwise convert every amount behind a bar that
  // says INR, which is the mismatch this whole control exists to prevent.
  React.useEffect(() => {
    setViewCurrencyChoice(null);
  }, [setViewCurrencyChoice]);
  useBranding(bootstrap?.society.name, bootstrap?.society);
  const platform = usePlatformGeneral();
  const brand = useBrandAssets(bootstrap?.society);
  const display = useBrandDisplay(bootstrap?.society);
  const logoHeight = useBrandLogoHeight(bootstrap?.society);
  const showLogo = display !== 'name';
  /**
   * "Logo only" hides the society's name — but only when the logo on screen is
   * the society's own. A society that has not uploaded one shows the platform's,
   * and a sidebar with the platform mark and no name is the same sidebar in
   * every society: there would be nothing on screen saying which one you are in.
   */
  const showName = display !== 'logo' || !brand.logoIsOwn;
  // Subscribing here re-renders the shell and everything under it when the
  // currency changes. `formatMoney` reads the choice at render time, so without
  // a subscription the amounts kept their old currency until a page reload.
  useCurrencyStore((state) => state.code);
  const i18n = useLocaleSetting();
  const [collapsed, setCollapsed] = React.useState(
    () => localStorage.getItem(SIDEBAR_KEY) === 'true',
  );
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, String(collapsed));
  }, [collapsed]);

  // A route change should always close the mobile drawer, or the user taps a
  // link and stares at the overlay.
  React.useEffect(() => setMobileOpen(false), [location.pathname]);

  const features = bootstrap?.features ?? {};
  const isReadOnly = bootstrap?.subscription.isReadOnly ?? false;
  const trialDaysLeft = bootstrap?.subscription.trialDaysLeft ?? null;
  const needsPlan = bootstrap?.subscription.needsPlan ?? false;

  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (!item.role || hasRole(item.role)) &&
        // A feature the plan lacks is hidden rather than shown-and-blocked; a
        // dead link is worse than an absent one.
        (!item.feature || features[item.feature] !== false),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <div className="flex min-h-screen bg-surface-variant">
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-outline bg-surface transition-all duration-200',
          collapsed ? 'w-16' : 'w-60',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex h-14 items-center gap-2.5 border-b border-outline px-4">
            {!showName && !collapsed && brand.logo ? (
              // Logo on its own: given the width the name has vacated, the same
              // way the platform console draws it. A wordmark carries the name
              // inside the image, and squeezing one into a 32px square is what
              // made it unreadable.
              <img
                src={brand.logo}
                alt={activeSociety?.societyName ?? ''}
                style={{ height: logoHeight }}
                className="min-w-0 max-w-[168px] flex-1 object-contain object-left"
              />
            ) : (
              <>
                {showLogo ? (
                  <div
                    style={{ width: logoHeight, height: logoHeight }}
                    className="flex shrink-0 items-center justify-center overflow-hidden rounded-sk-md bg-sk-brand"
                  >
                    {brand.logo ? (
                      <img src={brand.logo} alt="" className="size-full object-contain" />
                    ) : (
                      <span className="font-display text-sm font-extrabold text-white">
                        {(activeSociety?.societyName ?? platform?.siteName ?? 'S').charAt(0)}
                      </span>
                    )}
                  </div>
                ) : null}
                {showName && !collapsed ? (
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-sm font-bold leading-tight">
                      {activeSociety?.societyName ?? platform?.siteName ?? ''}
                    </p>
                    <p className="truncate text-2xs text-ink-3">{activeSociety?.city ?? t('Society admin')}</p>
                  </div>
                ) : null}
              </>
            )}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden"
            aria-label={t('Close menu')}
          >
            <X className="size-4 text-ink-3" />
          </button>
        </div>

        <nav className="sk-scroll flex-1 overflow-y-auto px-2 py-3">
          {visibleSections.map((section) => (
            <div key={section.heading} className="mb-4">
              {!collapsed ? <p className="sk-label px-2.5 pb-1.5">{section.heading}</p> : null}
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const count = item.badgeKey ? counts[item.badgeKey] : 0;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        title={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 rounded-sk-md px-2.5 py-2 text-[13px] font-medium transition-colors',
                            isActive
                              ? 'bg-indigo-soft font-semibold text-indigo-ink'
                              : 'text-ink-2 hover:bg-surface-variant hover:text-ink-1',
                            collapsed && 'justify-center px-0',
                          )
                        }
                      >
                        <item.icon className="size-[18px] shrink-0" aria-hidden />
                        {!collapsed ? <span className="flex-1 truncate">{item.label}</span> : null}
                        {count > 0 ? (
                          <Badge tone={collapsed ? 'danger' : 'indigo'} className={collapsed ? 'absolute ml-6 mt-[-14px]' : ''}>
                            {count > 99 ? '99+' : count}
                          </Badge>
                        ) : null}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-outline p-2">
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="hidden w-full items-center gap-2.5 rounded-sk-md px-2.5 py-2 text-[13px] text-ink-3 hover:bg-surface-variant hover:text-ink-1 lg:flex"
          >
            <PanelLeftClose
              className={cn('size-[18px] transition-transform', collapsed && 'rotate-180')}
              aria-hidden
            />
            {!collapsed ? <span>{t('Collapse')}</span> : null}
          </button>
        </div>
      </aside>

      {mobileOpen ? (
        <button
          type="button"
          aria-label={t('Close menu')}
          className="fixed inset-0 z-30 bg-ink-1/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      {/* ── Main ────────────────────────────────────────────────────────── */}
      <div className={cn('flex min-w-0 flex-1 flex-col transition-all', collapsed ? 'lg:pl-16' : 'lg:pl-60')}>
        {/* Impersonation banner — deliberately loud and permanently visible
           . A support engineer must never forget they are
            acting as someone else. */}
        {isImpersonating ? (
          <div className="flex items-center justify-between gap-3 bg-danger px-4 py-2 text-white">
            <div className="flex items-center gap-2 text-[13px] font-semibold">
              <AlertTriangle className="size-4" aria-hidden />
              <span>
                {t('You are viewing this society as')}{' '}{user?.name}{t('. Every action is logged')}
                {impersonationEndsAt
                  ? t('and this session ends at {0}', [new Date(impersonationEndsAt).toLocaleTimeString('en-IN')])
                  : ''}
                .
              </span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              className="border-white/30 bg-white/10 text-white hover:bg-white/20"
              onClick={() => {
                signOut();
                navigate('/platform/societies');
              }}
            >
              {t('End session')}
            </Button>
          </div>
        ) : null}

        {needsPlan ? (
          <div className="flex items-center gap-2 bg-indigo-soft px-4 py-2 text-[13px] font-medium text-indigo-ink">
            <Sparkles className="size-4 shrink-0" aria-hidden />
            <span className="flex-1">
              {t('Your society is not active yet. Choose a plan and pay to activate it — until then the console is read-only.')}
            </span>
            {hasRole('SOCIETY_ADMIN') ? (
              <Button size="sm" onClick={() => navigate('/subscription')}>
                {t('Choose a plan')}
              </Button>
            ) : null}
          </div>
        ) : isReadOnly ? (
          <div className="flex items-center gap-2 bg-warning-soft px-4 py-2 text-[13px] font-medium text-warning-ink">
            <AlertTriangle className="size-4 shrink-0" aria-hidden />
            <span className="flex-1">
              {t('Your society is read-only because the subscription lapsed. Residents can still pay their dues.')}
            </span>
            {hasRole('SOCIETY_ADMIN') ? (
              <Button size="sm" variant="secondary" onClick={() => navigate('/subscription')}>
                {t('Renew now')}
              </Button>
            ) : null}
          </div>
        ) : trialDaysLeft !== null && trialDaysLeft <= 7 ? (
          <div className="flex items-center gap-2 bg-indigo-soft px-4 py-2 text-[13px] font-medium text-indigo-ink">
            <Sparkles className="size-4 shrink-0" aria-hidden />
            <span className="flex-1">
              {trialDaysLeft > 0
                ? t('Your free trial ends in {0} day{1}.', [trialDaysLeft, trialDaysLeft === 1 ? '' : 's'])
                : t('Your free trial ends today.')}{' '}
              {t('Pick a plan to keep everything running.')}
            </span>
            <Button size="sm" onClick={() => navigate('/subscription')}>
              {t('Choose a plan')}
            </Button>
          </div>
        ) : null}

        {/* ── Top bar ───────────────────────────────────────────────────── */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-outline bg-surface/85 px-4 backdrop-blur">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden"
            aria-label={t('Open menu')}
          >
            <Menu className="size-5 text-ink-2" />
          </button>

          <div className="relative hidden max-w-md flex-1 md:block">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
            <input
              type="search"
              placeholder={t('Search members, complaints, bills…')}
              className="h-9 w-full rounded-sk-md border border-outline bg-surface-variant pl-9 pr-3 text-sm placeholder:text-ink-3 focus-visible:border-indigo focus-visible:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/20"
            />
          </div>

          <div className="ml-auto flex min-w-0 shrink items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              aria-label={
                counts.unreadNotifications > 0
                  ? t('Notifications, {0} unread', [counts.unreadNotifications])
                  : t('Notifications')
              }
              className="relative"
              onClick={() => setNotificationsOpen(true)}
            >
              {/* The badge sits on the bell's own corner, not the button's —
                  the button has padding around the icon and a badge at its
                  edge floats off to the side. */}
              <span className="relative inline-flex">
                <Bell className="size-[18px]" />
                {counts.unreadNotifications > 0 ? (
                  <span
                    aria-hidden
                    className="absolute -right-2.5 -top-2.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-danger px-1 font-num text-[9px] font-bold leading-none text-white ring-2 ring-surface"
                  >
                    {counts.unreadNotifications > 99 ? '99+' : counts.unreadNotifications}
                  </span>
                ) : null}
              </span>
            </Button>

            <CurrencySwitch persist className="hidden sm:inline-flex" />
            <LanguageSwitch locale={i18n.locale} enabled={i18n.enabled} className="hidden sm:inline-flex" />
            <span className="hidden sm:contents">
              <ThemeToggle />
              <FullscreenToggle />
            </span>

            {/* The society's own Support page — tickets to the platform team.
                The help-centre link, when set, is offered from that page. */}
            <Button variant="ghost" size="icon" aria-label={t('Help and support')} asChild>
              <Link to="/support">
                <LifeBuoy className="size-[18px]" />
              </Link>
            </Button>

            <UserMenu
              name={user?.name ?? 'Account'}
              email={user?.email ?? null}
              roles={roles}
              avatarUrl={user?.avatarUrl ?? null}
              onSignOut={() => {
                signOut();
                navigate('/login');
              }}
            />
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <React.Fragment key={i18n.key}>
            <Outlet />
          </React.Fragment>
        </main>
      </div>

      <NotificationsPanel open={notificationsOpen} onOpenChange={setNotificationsOpen} />
    </div>
  );
}

function UserMenu({
  name,
  email,
  roles,
  avatarUrl,
  onSignOut,
}: {
  name: string;
  email: string | null;
  roles: string[];
  avatarUrl: string | null;
  onSignOut: () => void;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-1.5 rounded-sk-md px-1.5 py-1 hover:bg-surface-variant"
      >
        <Avatar name={name} src={avatarUrl} size={30} />
        <ChevronDown className="size-3.5 text-ink-3" aria-hidden />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 w-60 rounded-sk-lg border border-outline bg-surface p-1.5 shadow-sk-lg"
        >
          <div className="border-b border-outline-soft px-2.5 pb-2.5 pt-1.5">
            <p className="truncate text-sm font-semibold">{name}</p>
            {email ? <p className="truncate text-xs text-ink-3">{email}</p> : null}
            <div className="mt-1.5 flex flex-wrap gap-1">
              {roles.slice(0, 3).map((role) => (
                <Badge key={role} tone="indigo">
                  {role.replace(/_/g, ' ').toLowerCase()}
                </Badge>
              ))}
            </div>
          </div>
          <NavLink
            to="/settings/profile"
            role="menuitem"
            className="flex items-center gap-2 rounded-sk-sm px-2.5 py-2 text-[13px] text-ink-2 hover:bg-surface-variant"
          >
            <Settings className="size-4" aria-hidden />{' '}{t('Profile & preferences')}
          </NavLink>
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            className="flex w-full items-center gap-2 rounded-sk-sm px-2.5 py-2 text-[13px] text-danger-ink hover:bg-danger-soft"
          >
            <LogOut className="size-4" aria-hidden />{' '}{t('Sign out')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
